import {
  formatAmount,
  formatTimestampUTC,
  formatTokenAmount,
  getInitials
} from '@/helpers/formatters';
import type { TransactionDTO } from '@/references/axios/support-chat/types';
import {
  AmountMode,
  type Transaction,
  TransactionAmountType,
  TransactionType
} from '@/references/axios/transactions/types';
import { t } from '@/references/i18n';
import type { Token } from '@/references/tokens';

export function getFallbackText(tx: Transaction | undefined): string {
  if (tx === undefined) {
    return '';
  }

  switch (tx.type) {
    case TransactionType.OTC:
      return getInitials(tx.beneficiary.name);
    default:
      return tx.id;
  }
}

export function getTitle(tx: Transaction | undefined): string {
  if (tx === undefined) {
    return '';
  }

  switch (tx.type) {
    case TransactionType.OTC:
      return t('activity.title.otc', { fullName: tx.beneficiary.name });
    default:
      return tx.type;
  }
}

export function getDescription(tx: Transaction | undefined): string | undefined {
  if (tx === undefined) {
    return undefined;
  }

  return formatTimestampUTC(tx.createdAt);
}

export function getFallbackImage(tx: Transaction | undefined): string | undefined {
  if (tx === undefined) {
    return undefined;
  }

  // add logic here if more tx types appear

  return undefined;
}

export function getLoaderColor(tx: Transaction | undefined): string | undefined {
  if (tx === undefined) {
    return undefined;
  }

  if (tx.flags.failed) {
    return 'var(--color-red)';
  }

  switch (tx.type) {
    case TransactionType.OTC:
      return 'var(--color-green)';
    default:
      return undefined;
  }
}

export function getTopAmount(tx: Transaction | undefined): string {
  if (tx === undefined) {
    return '';
  }

  switch (tx.type) {
    case TransactionType.OTC:
      return formatAmount(
        tx.totalAmount.amount,
        tx.totalAmount.decimals,
        tx.totalAmount.currency,
        AmountMode.SymbolFirst
      );
    default:
  }

  const fallback = tx as unknown as Transaction;

  if ((fallback.totalAmount.type as TransactionAmountType) === TransactionAmountType.Token) {
    return formatTokenAmount(
      fallback.totalAmount.amount,
      fallback.totalAmount.decimals,
      fallback.totalAmount.currency,
      AmountMode.CodeLast,
      fallback.flags.incoming
    );
  }

  return formatAmount(
    fallback.totalAmount.amount,
    fallback.totalAmount.decimals,
    fallback.totalAmount.currency,
    AmountMode.SymbolFirst,
    fallback.flags.incoming
  );
}

export function isTopAmountBordered(tx: Transaction | undefined): boolean {
  if (tx === undefined) {
    return false;
  }

  switch (tx.type) {
    case TransactionType.OTC:
      return tx.flags.bordered ?? false;
    default:
      return false;
  }
}

export function isTopAmountWithSkeleton(tx: Transaction | undefined): boolean {
  if (tx === undefined) {
    return false;
  }

  switch (tx.type) {
    case TransactionType.OTC:
      return tx.totalAmount.skeleton ?? false;
    default:
      return false;
  }
}

export function isTopAmountStrikeThrough(tx: Transaction | undefined): boolean {
  if (tx === undefined) {
    return false;
  }

  return tx.flags.strikethrough ?? false;
}

export function getBottomAmount(tx: Transaction | undefined): string | undefined {
  if (tx === undefined) {
    return undefined;
  }

  // add logic here if more tx types appear

  return undefined;
}

export function getBottomAmountToken(tx: Transaction | undefined): Token | undefined {
  if (tx === undefined) {
    return undefined;
  }

  // add logic here if more tx types appear

  return undefined;
}

export function isBottomAmountWithSkeleton(tx: Transaction | undefined): boolean {
  if (tx === undefined) {
    return false;
  }

  // add logic here if more tx types appear

  return false;
}

export function isBottomAmountStrikeThrough(tx: Transaction | undefined): boolean {
  if (tx === undefined) {
    return false;
  }

  // add logic here if more tx types appear

  return false;
}

export function getTokens(tx: Transaction | undefined): [Token, Token] | undefined {
  if (tx === undefined) {
    return undefined;
  }

  // add logic here if more tx types appear

  return undefined;
}

export function getFullTransactionByChatTransaction(
  transactions: Transaction[],
  chatTransaction: TransactionDTO
): Transaction | undefined {
  return transactions.find((tx) => {
    return tx.id === chatTransaction.offChainId || tx.id === chatTransaction.onChainHash;
  });
}
